/** What a catalog item's key names: a Unity Catalog catalog, and the workspace P0 reaches it through. */
export type CatalogKeyParts = { catalog: string; workspaceId: string };

// A workspace ID is all digits, and a catalog name has no dot, slash or
// whitespace, so the last `@` always separates the two.
const CATALOG_KEY_PATTERN = /^([^\s./]+)@(\d+)$/;

/**
 * Renders a catalog item's key, `<catalog>@<workspaceId>`, e.g.
 * `main@1234567890123456`.
 *
 * Catalog names are unique only within a metastore, so the key adds the
 * workspace. Two installed catalog items with the same catalog name, in
 * workspaces attached to the same metastore, would name the same catalog, so
 * installing the second is refused.
 */
export const renderCatalogKey = ({ catalog, workspaceId }: CatalogKeyParts) =>
  `${catalog}@${workspaceId}`;

/** How a catalog key is written, for copy that describes one: `<catalog>@<workspace ID>`. */
export const CATALOG_KEY_FORMAT = renderCatalogKey({
  catalog: "<catalog>",
  workspaceId: "<workspace ID>",
});

/** The catalog name and workspace ID in a catalog item's key, or `undefined` if `key` isn't one. */
export const parseCatalogKey = (key: string): CatalogKeyParts | undefined => {
  const [, catalog, workspaceId] = CATALOG_KEY_PATTERN.exec(key) ?? [];
  return catalog && workspaceId ? { catalog, workspaceId } : undefined;
};
